import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import buddhistEra from "dayjs/plugin/buddhistEra";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import IconArrow from "../../../../../assets/images/icon-arrow-3.svg";

dayjs.extend(buddhistEra);
dayjs.extend(quarterOfYear);

const Container = styled.div`
    width: 100%;
    position: relative;

    .label {
        color: #2A2A2A;
        font-size: 15px;
        font-weight: 500;
        line-height: 22.43px;
        text-align: left;
        margin-bottom: 8px;
    }

    .trigger {
        box-sizing: border-box;
        width: 100%;
        min-height: 40px;
        padding: 0 16px;
        background: #F5F5F5;
        border-radius: 30px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        transition: all 0.2s ease;
        color: #25368D;
        font-size: 15px;
        font-weight: 500;
        line-height: 22.43px;
    }

    .trigger:hover { background: #FBE25033; }
    .trigger .placeholder { color: #838BB4; font-weight: 400; }
    .trigger .arrow { width: 16px; height: 16px; margin-left: 8px; flex-shrink: 0; }
`;

const Popover = styled.div`
    position: absolute;
    top: 76px;
    left: 0;
    min-width: 100%;
    width: max-content;
    max-width: 320px;
    background: #FFFFFF;
    border: 1px solid #B5B5B533;
    box-shadow: 0px 4px 12px 0px #60606020;
    border-radius: 12px;
    padding: 6px;
    z-index: 99;
    max-height: 360px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 2px;

    .item {
        padding: 8px 14px;
        border-radius: 8px;
        cursor: pointer;
        color: #505050;
        font-size: 14px;
        font-weight: 500;
        white-space: nowrap;
    }

    .item:hover { background: #FBE25033; color: #25368D; }
    .item.active { background: #25368D; color: #FFFFFF; }
    .item.divider { border-top: 1px solid #F2F2F2; margin-top: 4px; padding-top: 12px; }
`;

function buildOptions() {
    const today = dayjs();
    const range = (start, end) => ({
        start: start.startOf("day").toISOString(),
        end: end.endOf("day").toISOString(),
    });
    const yearLabel = (y) => `ปี ${y.format("BBBB")}`;
    return [
        { id: "today",       label: "วันนี้",          range: range(today, today) },
        { id: "yesterday",   label: "เมื่อวาน",        range: range(today.subtract(1, "day"), today.subtract(1, "day")) },
        { id: "last7",       label: "7 วันที่ผ่านมา",   range: range(today.subtract(6, "day"), today) },
        { id: "last30",      label: "30 วันที่ผ่านมา",  range: range(today.subtract(29, "day"), today) },
        { id: "thisMonth",   label: "เดือนนี้",        range: range(today.startOf("month"), today) },
        { id: "lastMonth",   label: "เดือนที่แล้ว",     range: range(today.subtract(1, "month").startOf("month"), today.subtract(1, "month").endOf("month")) },
        { id: "thisQuarter", label: "ไตรมาสนี้",       range: range(today.startOf("quarter"), today) },
        { id: "lastQuarter", label: "ไตรมาสที่แล้ว",    range: range(today.subtract(1, "quarter").startOf("quarter"), today.subtract(1, "quarter").endOf("quarter")) },
        { id: "thisYear",    label: `ปีนี้ (${yearLabel(today)})`,                       range: range(today.startOf("year"), today) },
        { id: "lastYear",    label: `ปีที่แล้ว (${yearLabel(today.subtract(1, "year"))})`, range: range(today.subtract(1, "year").startOf("year"), today.subtract(1, "year").endOf("year")) },
        { id: "custom",      label: "กำหนดช่วงเอง...", divider: true },
    ];
}

function fmtRange(start_date, end_date) {
    if (!start_date || !end_date) return null;
    return `${dayjs(start_date).format("DD/MM/BBBB")} - ${dayjs(end_date).format("DD/MM/BBBB")}`;
}

export default function PeriodSelector({ label, value, onChange, hideRangeWhenCustom = false }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const options = useMemo(buildOptions, []);

    useEffect(() => {
        function handler(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener("click", handler);
        return () => document.removeEventListener("click", handler);
    }, []);

    const current = options.find(o => o.id === value?.period);
    const triggerLabel = (() => {
        if (!current) return null;
        if (current.id === "custom") {
            if (hideRangeWhenCustom) return "กำหนดช่วงเอง";
            const r = fmtRange(value?.start_date, value?.end_date);
            return r ? `กำหนดช่วงเอง: ${r}` : "กำหนดช่วงเอง...";
        }
        return current.label;
    })();

    function handlePick(opt) {
        setOpen(false);
        if (opt.id === "custom") {
            onChange({ period: "custom" });
        } else {
            onChange({ period: opt.id, start_date: opt.range.start, end_date: opt.range.end });
        }
    }

    return (
        <Container ref={ref}>
            {label && <div className="label">{label}</div>}
            <div className="trigger" onClick={() => setOpen(!open)}>
                <div className={!triggerLabel ? "placeholder" : ""}>{triggerLabel || "เลือกช่วงเวลา"}</div>
                <img className="arrow" alt="arrow" src={IconArrow} />
            </div>
            {open && (
                <Popover onClick={(e) => e.stopPropagation()}>
                    {options.map((o) => (
                        <div
                            key={o.id}
                            className={`item ${value?.period === o.id ? "active" : ""} ${o.divider ? "divider" : ""}`}
                            onClick={() => handlePick(o)}
                        >
                            {o.label}
                        </div>
                    ))}
                </Popover>
            )}
        </Container>
    );
}
